/**
 * TTSSessionToggle — session-level voice playback for the AI Tutor.
 *
 * Acts as both a UI toggle AND an imperative audio queue. Parent grabs
 * a ref and calls `enqueue(text)` for every completed sentence as Claude
 * streams; the toggle fetches `/api/tts/speak` per chunk, queues the
 * resulting audio blob, and plays clips back in order. `finalize()`
 * marks the end of the current reply so `onSpeechEnded` fires after the
 * last clip drains (which is how MicRecorder gets its auto-listen cue
 * in hands-free mode).
 *
 * Why per-sentence? — ElevenLabs round-trip for a 15-word sentence is
 * ~1-2 s; round-trip for a 120-word reply is ~4-6 s. Chunking turns the
 * student's perceived "Claude → voice" latency from "wait for the whole
 * answer" into "wait for the first sentence" — a 60-70% reduction.
 *
 * Visual spec stays: elongated Brand-Gold pill labelled VOICE / VOICE ON
 * / SPEAKING beside the Send button.
 */
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle,
  useRef, useState,
} from 'react';
import { api } from '../lib/apiClient';

const STORAGE_KEY = 'tts_session_on';
const SPEED_STORAGE_KEY = 'tts_session_speed';
const SPEED_MIN = 0.7;
const SPEED_MAX = 1.2;
const SPEED_STEP = 0.05;
const GOLD = 'var(--gr-gold, #FCD359)';
const GOLD_DARK = 'var(--gr-gold-dark, #E0B73D)';
const GOLD_DEEP = 'var(--gr-gold-deep, #8C6C1F)';

let __sharedAudio = null;
function getSharedAudio() {
  if (typeof window === 'undefined') return null;
  if (!__sharedAudio) {
    __sharedAudio = new Audio();
    __sharedAudio.preload = 'auto';
  }
  return __sharedAudio;
}

let __healthPromise = null;
async function ttsHealth() {
  if (!__healthPromise) {
    __healthPromise = api.get('/api/tts/health')
      .then((r) => r?.data || null)
      .catch(() => null);
  }
  return __healthPromise;
}

const TTSSessionToggle = forwardRef(({ onSpeechEnded, testId = 'tts-session-toggle' }, ref) => {
  const [ready, setReady] = useState(null);
  const [on, setOn] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });
  // Per-user playback speed. `null` until we know the server default from
  // /api/tts/health; once known we fall back to that unless the user has
  // saved their own preference in localStorage.
  const [speed, setSpeed] = useState(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(SPEED_STORAGE_KEY);
    const parsed = raw ? parseFloat(raw) : NaN;
    return Number.isFinite(parsed) ? Math.max(SPEED_MIN, Math.min(SPEED_MAX, parsed)) : null;
  });
  const [playing, setPlaying] = useState(false);

  // ── Audio queue ─────────────────────────────────────────────────────
  // `clipQueue` is an array of pending blob-URL promises. Each promise
  // resolves to a string URL the <audio> element can play. Slots can be
  // pending (network in-flight) or ready — the player loop awaits the
  // promise so playback is ALWAYS in submission order.
  const clipQueueRef = useRef([]);            // array of Promise<string|null>
  const playerRunningRef = useRef(false);
  const finalizedRef = useRef(false);         // parent signalled "no more sentences"
  const onRef = useRef(on);
  useEffect(() => { onRef.current = on; }, [on]);
  // Keep a ref of `speed` so the imperative `enqueue` method always sends
  // the latest slider value without React stale-closure issues.
  const speedRef = useRef(speed);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  const onSpeechEndedRef = useRef(onSpeechEnded);
  useEffect(() => { onSpeechEndedRef.current = onSpeechEnded; }, [onSpeechEnded]);

  // Health probe once per page. Hydrates `ready` (boolean) AND seeds
  // `speed` with the server default if the user hasn't saved their own.
  useEffect(() => {
    ttsHealth().then((h) => {
      setReady(!!(h && h.ready));
      if (h && typeof h.default_speed === 'number') {
        setSpeed((cur) => (cur == null ? h.default_speed : cur));
      } else {
        // No server hint and no user pref → default to 0.9 (calm tutor).
        setSpeed((cur) => (cur == null ? 0.9 : cur));
      }
    });
  }, []);

  // Persist on/off + speed across reloads.
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  }, [on]);
  useEffect(() => {
    if (speed == null) return;
    try { window.localStorage.setItem(SPEED_STORAGE_KEY, String(speed)); } catch { /* ignore */ }
  }, [speed]);

  // Hard reset — abandons every queued/playing clip. Used when the user
  // toggles off, when a new student message is sent, or when streaming
  // ends with an error.
  const reset = useCallback(() => {
    // Revoke any urls already resolved to avoid blob-url leaks.
    for (const p of clipQueueRef.current) {
      Promise.resolve(p).then((url) => {
        if (url) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }
      });
    }
    clipQueueRef.current = [];
    finalizedRef.current = false;
    playerRunningRef.current = false;
    try {
      const audio = getSharedAudio();
      audio.pause();
      audio.currentTime = 0;
    } catch { /* ignore */ }
    setPlaying(false);
  }, []);

  // Run the audio queue: dequeue clips one at a time and play them. Loops
  // until the queue is drained AND `finalize()` has been called — at that
  // point we fire `onSpeechEnded` so MicRecorder can auto-listen.
  const startPlayerLoop = useCallback(() => {
    if (playerRunningRef.current) return;
    playerRunningRef.current = true;
    const audio = getSharedAudio();
    if (!audio) { playerRunningRef.current = false; return; }

    const loop = async () => {
      while (onRef.current) {
        const next = clipQueueRef.current.shift();
        if (!next) {
          if (finalizedRef.current) break;        // drained + no more coming
          await new Promise((r) => setTimeout(r, 60));  // wait for next enqueue
          continue;
        }
        let url = null;
        try { url = await next; } catch { url = null; }
        if (!url) continue;
        if (!onRef.current) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } break; }
        setPlaying(true);
        await new Promise((resolve) => {
          const cleanup = () => {
            audio.removeEventListener('ended', cleanup);
            audio.removeEventListener('pause', cleanup);
            try { URL.revokeObjectURL(url); } catch { /* ignore */ }
            resolve();
          };
          audio.addEventListener('ended', cleanup);
          audio.addEventListener('pause', cleanup);
          audio.src = url;
          audio.play().catch(() => cleanup());
        });
      }
      setPlaying(false);
      playerRunningRef.current = false;
      // Only emit "speech ended" if we drained naturally (not because the
      // user muted mid-playback). `finalizedRef` is our proxy for "the
      // tutor finished this reply" — without it, the cue would fire on
      // every empty-queue tick.
      if (onRef.current && finalizedRef.current && typeof onSpeechEndedRef.current === 'function') {
        onSpeechEndedRef.current();
      }
    };
    loop();
  }, []);

  // ── Imperative API ──────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    /**
     * Queue a chunk of assistant text for playback. Fires the network
     * request immediately so the next clip is preloading while the
     * current one plays. Returns the queued promise (useful for tests).
     */
    enqueue(text) {
      if (!onRef.current) return null;
      const clean = (text || '').trim();
      if (clean.length < 2) return null;
      // Send the live slider value with every chunk so changes mid-reply
      // take effect on the next chunk (cache keys include speed so each
      // distinct setting is independently memoised).
      const body = { text: clean };
      if (typeof speedRef.current === 'number') body.speed = speedRef.current;
      const promise = api.post('/api/tts/speak', body, { responseType: 'blob' })
        .then((res) => URL.createObjectURL(res.data))
        .catch(() => null);
      clipQueueRef.current.push(promise);
      startPlayerLoop();
      return promise;
    },
    /** Signal that no more chunks will arrive for the current reply. */
    finalize() {
      finalizedRef.current = true;
      startPlayerLoop();  // covers the "finalize-after-everything-was-empty" case
    },
    /** Abandon the entire queue + stop playback. Use on user-cancel /
     *  new question / stream error. */
    reset,
    /** Public read: is the toggle currently ON? */
    isOn() { return !!onRef.current; },
  }), [reset, startPlayerLoop]);

  const onClick = useCallback(() => {
    if (!ready) return;
    setOn((prev) => {
      const next = !prev;
      if (!next) {
        // Mute: drop everything in-flight.
        reset();
      }
      return next;
    });
  }, [ready, reset]);

  if (ready === false) return null;

  const tip = !ready
    ? 'Voice playback warming up…'
    : on
      ? playing
        ? 'Tutor is speaking — click to mute this session'
        : 'Voice is ON for this session — click to mute'
      : 'Click to enable tutor voice for this session';

  // Elongated-square pill. Brand Gold when ON; neutral steel when OFF;
  // gold-deep border when ON for extra contrast against white input row.
  const bg = on ? GOLD : '#f3f4f6';
  const fg = on ? GOLD_DEEP : '#374151';
  const border = on ? GOLD_DARK : 'rgba(0,0,0,0.12)';
  const shadow = playing
    ? `0 0 0 3px ${GOLD_DARK}66`
    : on
      ? `0 1px 2px rgba(0,0,0,0.08), 0 0 0 1px ${GOLD_DARK}`
      : '0 1px 2px rgba(0,0,0,0.08)';

  return (
    <span
      data-testid={`${testId}-wrap`}
      className="inline-flex items-center gap-2 self-stretch"
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!ready}
        title={tip}
        aria-label={tip}
        aria-pressed={on}
        data-testid={testId}
        className="inline-flex items-center justify-center gap-1.5 self-stretch transition-all"
        style={{
          minWidth: 78,
          paddingLeft: 10,
          paddingRight: 12,
          borderRadius: 6,
          background: bg,
          color: fg,
          border: `1px solid ${border}`,
          cursor: ready ? 'pointer' : 'not-allowed',
          boxShadow: shadow,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            opacity: playing ? 1 : 0.95,
          }}
        >
          {playing ? (
            <>
              <span style={{ width: 2, height: 6,  background: fg, display: 'inline-block', borderRadius: 1 }} />
              <span style={{ width: 2, height: 12, background: fg, display: 'inline-block', borderRadius: 1 }} />
              <span style={{ width: 2, height: 8,  background: fg, display: 'inline-block', borderRadius: 1 }} />
            </>
          ) : (
            <span style={{ fontSize: 14, lineHeight: 1 }}>{on ? '🔊' : '🔈'}</span>
          )}
        </span>
        <span>{on ? (playing ? 'Speaking' : 'Voice On') : 'Voice'}</span>
      </button>

      {on && speed != null && (
        <label
          data-testid={`${testId}-speed`}
          title={`Voice speed: ${speed.toFixed(2)}× (1.00× = native cadence)`}
          className="inline-flex items-center gap-1.5 self-stretch"
          style={{
            paddingLeft: 8,
            paddingRight: 10,
            borderRadius: 6,
            background: '#fffdf5',
            border: `1px solid ${GOLD_DARK}`,
            fontSize: 11,
            fontWeight: 600,
            color: GOLD_DEEP,
            letterSpacing: 0.2,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>⏱</span>
          <input
            type="range"
            min={SPEED_MIN}
            max={SPEED_MAX}
            step={SPEED_STEP}
            value={speed}
            data-testid={`${testId}-speed-input`}
            onChange={(e) => {
              const next = parseFloat(e.target.value);
              if (Number.isFinite(next)) {
                setSpeed(Math.max(SPEED_MIN, Math.min(SPEED_MAX, next)));
              }
            }}
            style={{ width: 80, accentColor: GOLD_DARK, cursor: 'pointer' }}
            aria-label="Voice playback speed"
          />
          <span
            data-testid={`${testId}-speed-value`}
            style={{ fontVariantNumeric: 'tabular-nums', minWidth: 32, textAlign: 'right' }}
          >
            {speed.toFixed(2)}×
          </span>
        </label>
      )}
    </span>
  );
});

TTSSessionToggle.displayName = 'TTSSessionToggle';
export default TTSSessionToggle;
